import { useEffect, useState } from 'react';
// import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import SaveIcon from '@material-ui/icons/Save';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import { saveGame, startGame } from '../../../services/Student';
import { Button } from '../../Buttons';
import { BlockContainer, ImageBlock } from '../index';
import GreenHouse from '../../Greenhouse';
import ParameterPanel from '../ParameterPanel';
import { kassimBlue } from '../../../style/theme';
import PanelWithHelp from '../../PanelWithHelp';
import { getHelpUrl } from '../../../services/shared';

const Preset = () => {
    const { t } = useTranslation();
    const { auth, reload } = useAuthContext();
    const { game, gameConfig, token, fetching } = auth;
    const { enqueueSnackbar } = useSnackbar();

    const [formValues, setFormValues] = useState({});
    const [busy, setBusy] = useState(false);

    const [greenhouseImages, setGreenhouseImages] = useState({
        background: 'achtergrond_dag_helder.png',
    });

    useEffect(() => {
        if (game) {
            const presetParameters = Object.entries(game).filter(
                ([key, value]) => {
                    const paramConfig = gameConfig[key];

                    return paramConfig?.type === 'preset';
                }
            );

            const initialValues = presetParameters.reduce((prev, cur) => {
                const [key, value] = cur;
                let valueIndex = value;
                if (
                    key === 'comp1.crop.@type' &&
                    typeof valueIndex === 'string'
                ) {
                    if (gameConfig[key]?.values) {
                        valueIndex = gameConfig[key]?.values.indexOf(value);
                    } else {
                        valueIndex = -1;
                    }
                    if (valueIndex < 0) {
                        valueIndex = 0;
                    }
                }
                return { ...prev, [key]: valueIndex };
            }, {});

            const initialGreenhouseImages = presetParameters
                .filter(([key, value]) => {
                    const paramConfig = gameConfig[key];
                    return (
                        paramConfig.control === 'combobox' &&
                        paramConfig.bitmap &&
                        paramConfig.bitmap.length > value
                    );
                })
                .reduce((prev, [key, value]) => {
                    const paramConfig = gameConfig[key];
                    return { ...prev, [key]: paramConfig.bitmap[value] };
                }, {});
            setFormValues(initialValues);

            setGreenhouseImages((prev) => ({
                ...prev,
                ...initialGreenhouseImages,
            }));
        }
    }, [game, gameConfig]);

    const handleInputChange = (inputKey, paramConfig) => (value) => {
        setFormValues((prevValues) => {
            return {
                ...prevValues,
                [inputKey]: value,
            };
        });
        if (
            paramConfig.control === 'combobox' &&
            paramConfig.bitmap &&
            paramConfig.bitmap.length > value
        ) {
            setGreenhouseImages((prev) => ({
                ...prev,
                [inputKey]: paramConfig.bitmap[value],
            }));
        }
    };

    const handleSavePresets = () => {
        let mounted = true;
        const mergedGameData = Object.assign({}, game, formValues);
        setBusy(true);
        saveGame(token, mergedGameData)
            .catch((err) => {
                enqueueSnackbar(
                    <div>
                        <p>{t('error.gameNotSaved')}</p>
                        <p>{err.message}</p>
                    </div>,
                    {
                        variant: 'error',
                    }
                );
            })
            .finally(() => {
                if (mounted) {
                    setBusy(false);
                }
            });

        return () => {
            mounted = false;
        };
    };

    const handleStartGame = () => {
        let mounted = true;
        const mergedGameData = Object.assign({}, game, formValues);
        setBusy(true);

        saveGame(token, mergedGameData)
            .then(() => {
                if (mounted) {
                    startGame(token)
                        .catch((err) => {
                            enqueueSnackbar(
                                <div>
                                    <p>{t('error.gameNotStarted')}</p>
                                    <p>{err.message}</p>
                                </div>,
                                {
                                    variant: 'error',
                                }
                            );
                        })
                        .then(() => {
                            reload();
                        })
                        .finally(() => {
                            if (mounted) {
                                setBusy(false);
                            }
                        });
                }
            })
            .catch((err) => {
                enqueueSnackbar(
                    <div>
                        <p>{t('error.gameNotSaved')}</p>
                        <p>{err.message}</p>
                    </div>,
                    {
                        variant: 'error',
                    }
                );
                if (mounted) {
                    setBusy(false);
                }
            });

        return () => {
            mounted = false;
        };
    };

    if (!game || !formValues) return null;
    const { language } = game;

    const getFullHelpUrl = (param) =>
        getHelpUrl(param, gameConfig, auth.languageId);

    return (
        <BlockContainer>
            <PanelWithHelp
                style={{ height: '100%' }}
                helpUrl={getFullHelpUrl(
                    gameConfig?.['@help']?.user?.fixedparams
                )}
            >
                <ParameterPanel
                    title={t('game.Preset parameters')}
                    formValues={formValues}
                    gameConfig={gameConfig}
                    language={language}
                    buttons={[
                        <Button
                            key="save"
                            disabled={busy || fetching}
                            onClick={handleSavePresets}
                            startIcon={<SaveIcon />}
                            variant="outlined"
                            color="primary"
                        >
                            {t('buttons.save')}
                        </Button>,
                        <Button
                            key="start"
                            disabled={busy || fetching}
                            onClick={handleStartGame}
                            startIcon={[
                                <SaveIcon
                                    style={{ fontSize: '18px' }}
                                    key="save-icon"
                                />,
                                <PlayArrowIcon
                                    style={{ fontSize: '18px' }}
                                    key="play-icon"
                                />,
                            ]}
                            backgroundColor={kassimBlue}
                            frontColor="#fff"
                        >
                            {t('buttons.startGame')}
                        </Button>,
                    ]}
                    handleInputChange={handleInputChange}
                />
            </PanelWithHelp>
            <ImageBlock>
                <GreenHouse
                    images={Object.values(greenhouseImages).filter(
                        (img) => img
                    )}
                />
            </ImageBlock>
        </BlockContainer>
    );
};

export default Preset;
